/* Performance Analytics — dashboard definitions.
 *
 * Each standard dashboard is a `DashPage` (KPIs + ordered content blocks),
 * ported 1:1 from the design mock-up's `pageRenderers`.  Interactive pages
 * (At-Risk heatmap, Salesperson Scorecard, Client Contribution) are bespoke
 * components — see PerformanceAnalyticsPage.tsx.  All numbers are the design's
 * seeded mock values (no API — per the request). */
import {
  CLIENTS, CLIENT_NAMES, TESTS, ANALYSTS, INSTRUMENTS, REPS, MONTHS, DAYS28, WEEKS12,
  PALETTE as C, series, one, pick, det, seedRand,
} from './mockData';
import type { DashPage, Kpi, Cell } from './types';

const k = (label: string, value: string, delta: string, dir: Kpi['dir']): Kpi => ({ label, value, delta, dir });
const CLIENT_COLORS = [C.orange, C.blue, C.green, C.amber, C.red, C.navy, C.orangeLight, C.blueLight];
const money = (n: number) => `$${n.toLocaleString()}`;

export function buildPages(): Record<string, DashPage> {
  const P: Record<string, DashPage> = {};

  /* ═══════════════ LAB · Throughput & Workload ═══════════════ */
  P['lab-throughput'] = {
    title: 'Samples Received vs Completed',
    desc: 'Daily intake against completion volume — gap signals building backlog.',
    kpis: [
      k('Received (28d)', '1,842', '▲ 6.2% vs prior period', 'up'),
      k('Completed (28d)', '1,761', '▼ 1.8% vs prior period', 'down'),
      k('Net Gap', '+81', 'Backlog growing', 'down'),
      k('Completion Rate', '95.6%', '— flat vs prior', 'flat'),
    ],
    blocks: [
      { kind: 'panel', panel: {
        title: 'Daily Received vs Completed — last 28 days', sub: 'Sample counts, all clients combined', tall: true,
        chart: { type: 'line', labels: DAYS28, series: [
          { name: 'Received', color: C.orange, data: series(28, 68, 14, 11) },
          { name: 'Completed', color: C.blue, data: series(28, 65, 13, 22) },
        ] },
        legend: [{ label: 'Received', color: C.orange }, { label: 'Completed', color: C.blue }],
        note: 'The orange line is samples received each day; the blue line is samples completed. When orange runs above blue for several days in a row, backlog is building.',
      } },
      { kind: 'panelRow', cols: '2', panels: [
        { title: 'Received by Client',
          chart: { type: 'bar', labels: [...CLIENTS], data: CLIENTS.map((_, i) => one(230, 80, 30 + i)), color: C.orange },
          note: 'Bar height shows how many samples each client submitted this period. Taller bars are higher-volume clients.' },
        { title: 'Completion Rate Trend (12wk)',
          chart: { type: 'line', labels: WEEKS12, yMin: 80, yMax: 100, series: [{ color: C.green, data: series(12, 95, 3, 40), fill: true }] },
          note: "Each point is the week's completion rate (completed ÷ received). A flat or rising line near 100% means the lab is keeping pace with intake." },
      ] },
    ],
  };

  /* ── Turnaround Time (shares tatVals between chart + table) ── */
  const tatVals = TESTS.map((_, i) => +(one(4.5, 2.5, 50 + i) + 3).toFixed(1));
  P['lab-tat'] = {
    title: 'Turnaround Time',
    desc: 'Average and median TAT against SLA targets, by test category.',
    kpis: [
      k('Avg TAT (all tests)', '4.6 days', '▲ 0.3d vs target', 'up'),
      k('Median TAT', '3.9 days', '— in line', 'flat'),
      k('% Within SLA', '88.4%', '▼ 2.1pp', 'down'),
      k('Worst Test Category', 'DBPs', '7.2d avg', 'down'),
    ],
    blocks: [
      { kind: 'panelRow', cols: '2', panels: [
        { title: 'Avg TAT by Test (days)', tall: true,
          chart: { type: 'bar', labels: [...TESTS], data: tatVals, color: C.orange, horizontal: true },
          note: 'Bars show average turnaround time per test, in days. Longer bars are slower tests and the first candidates to investigate if TAT is slipping.' },
        { title: '% Within SLA — 12 week trend', tall: true,
          chart: { type: 'line', labels: WEEKS12, yMin: 75, yMax: 100, series: [{ color: C.blue, data: series(12, 89, 4, 60), fill: true }] },
          note: 'The line tracks the percentage of samples completed within their SLA target each week. A downward trend signals turnaround is falling behind commitments.' },
      ] },
      { kind: 'table', table: {
        cols: ['Test', 'Avg TAT', 'Median', 'SLA Target', '% Within SLA'],
        rows: TESTS.map((t, i): Cell[] => {
          const avg = tatVals[i] ?? 0, med = +(avg - 0.6).toFixed(1), target = 5.0;
          const pct = Math.max(70, Math.round(100 - (avg - target) * 8));
          const pill = pct >= 90 ? 'green' : pct >= 80 ? 'amber' : 'red';
          return [t, `${avg}d`, `${med}d`, `${target}d`, { text: `${pct}%`, pill }];
        }),
      } },
    ],
  };

  P['lab-backlog'] = {
    title: 'Backlog / WIP Age',
    desc: 'Open samples bucketed by age — flags samples at risk of SLA breach.',
    kpis: [
      k('Total Open Samples', '312', '▲ 14 vs last week', 'down'),
      k('0–2 Days', '198', '63% of WIP', 'flat'),
      k('3–5 Days', '76', '24% of WIP', 'flat'),
      k('6+ Days (at risk)', '38', '12% of WIP', 'down'),
    ],
    blocks: [
      { kind: 'panelRow', cols: '2', panels: [
        { title: 'WIP by Age Bucket', tall: true,
          chart: { type: 'bar', labels: ['0–2 Days', '3–5 Days', '6+ Days'], data: [198, 76, 38], color: [C.green, C.amber, C.red] },
          note: 'Each bar is the count of open samples in that age range. The red "6+ Days" bar is the one to watch — those samples are closest to breaching SLA.' },
        { title: 'Backlog Trend — 12 weeks', tall: true,
          chart: { type: 'line', labels: WEEKS12, series: [{ color: C.red, data: series(12, 280, 30, 70), fill: true }] },
          note: 'This line shows total open (unfinished) samples by week. A rising line means the lab is falling behind; a flat or falling line means backlog is under control.' },
      ] },
    ],
  };

  P['lab-queue'] = {
    title: 'Analysis Queue Depth',
    desc: 'Pending analyses by instrument and analyst — identifies bottlenecks.',
    kpis: [
      k('Total Queued', '247', '— stable', 'flat'),
      k('Busiest Instrument', 'ICP-MS 1', '58 queued', 'down'),
      k('Avg Queue Wait', '1.8 days', '▲ 0.2d', 'up'),
      k('Idle Instruments', '1', 'Spec-1', 'flat'),
    ],
    blocks: [
      { kind: 'panelRow', cols: '2', panels: [
        { title: 'Queue Depth by Instrument', tall: true,
          chart: { type: 'bar', labels: [...INSTRUMENTS], data: INSTRUMENTS.map((_, i) => one(35, 30, 80 + i)), color: C.orange, horizontal: true },
          note: 'Bar length shows how many analyses are waiting on each instrument. The longest bar is the current bottleneck.' },
        { title: 'Queue Depth by Analyst', tall: true,
          chart: { type: 'bar', labels: [...ANALYSTS], data: ANALYSTS.map((_, i) => one(40, 25, 90 + i)), color: C.blue, horizontal: true },
          note: 'Bar length shows how many analyses are queued for each analyst. Use this alongside the instrument chart to see if a backlog is a people problem or an equipment problem.' },
      ] },
    ],
  };

  P['lab-volume'] = {
    title: 'Sample Volume by Client',
    desc: 'Monthly sample submission volume across the client base.',
    kpis: [
      k('Total Samples (MTD)', '1,842', '▲ 6.2%', 'up'),
      k('Top Client', 'DWO', '412 samples', 'flat'),
      k('Active Clients', '8', '— stable', 'flat'),
      k('Avg per Client', '230', '▲ 4.1%', 'up'),
    ],
    blocks: [
      { kind: 'panel', panel: {
        title: 'Monthly Volume by Client — 12 months', tall: true,
        chart: { type: 'groupBar', labels: MONTHS, stacked: true,
          series: CLIENTS.map((c, i) => ({ name: c, color: CLIENT_COLORS[i] ?? C.grey, data: series(12, 30, 12, 100 + i * 5) })) },
        note: "Each colored band is one client's monthly sample volume, stacked to show total lab volume. A growing or shrinking band shows that client's trend; the height of the whole stack shows overall demand.",
      } },
    ],
  };

  /* ═══════════════ LAB · Quality & Compliance ═══════════════ */
  const passRates = TESTS.map((_, i) => +(90 + one(8, 6, 150 + i)).toFixed(1));
  P['lab-qcpass'] = {
    title: 'QC Batch Pass/Fail Rate',
    desc: 'QC batch outcomes by analyte/method — flags methods needing attention.',
    kpis: [
      k('Pass Rate (30d)', '96.8%', '▲ 0.4pp', 'up'),
      k('Batches Run', '412', '— stable', 'flat'),
      k('Failed Batches', '13', '▼ 2 vs prior', 'down'),
      k('Worst Method', 'EPA 524.2', '91.2% pass', 'down'),
    ],
    blocks: [
      { kind: 'panelRow', cols: '2', panels: [
        { title: 'Pass Rate by Method', tall: true,
          chart: { type: 'bar', labels: [...TESTS], data: passRates, horizontal: true, yMin: 80, yMax: 100,
            color: passRates.map(p => (p >= 95 ? C.green : p >= 90 ? C.amber : C.red)) },
          note: 'Bar height is the QC pass rate for that method. Green bars are healthy (95%+); amber and red bars need investigation.' },
        { title: 'Pass Rate Trend — 12 weeks', tall: true,
          chart: { type: 'line', labels: WEEKS12, yMin: 88, yMax: 100, series: [{ color: C.green, data: series(12, 96, 2, 160), fill: true }] },
          note: "This line tracks the lab's overall QC pass rate by week. A dip below the normal range is worth investigating before it becomes a pattern." },
      ] },
    ],
  };

  P['lab-holdtime'] = {
    title: 'Holding Time Violations',
    desc: 'Samples analyzed outside method-required holding time windows.',
    kpis: [
      k('Violations (30d)', '9', '▼ 3 vs prior', 'down'),
      k('Violation Rate', '0.49%', '▼ 0.1pp', 'down'),
      k('Worst Test', 'Total Coliform', '4 violations', 'down'),
      k('Most Affected Client', 'HHD', '3 violations', 'flat'),
    ],
    blocks: [
      { kind: 'panelRow', cols: '2', panels: [
        { title: 'Violations by Test', tall: true,
          chart: { type: 'bar', labels: [...TESTS], data: TESTS.map((_, i) => one(1.5, 3, 170 + i)), color: C.red, horizontal: true },
          note: 'Bar height shows how many holding-time violations occurred for that test. Tests with consistently tall bars may need workflow or scheduling changes.' },
        { title: 'Violations Trend — 12 weeks', tall: true,
          chart: { type: 'bar', labels: WEEKS12, data: series(12, 1, 2, 180), color: C.amber },
          note: 'Each bar is the count of holding-time violations that week. Occasional single-digit bars are normal; a rising trend points to a process issue upstream.' },
      ] },
    ],
  };

  P['lab-rerun'] = {
    title: 'Reanalysis / Rerun Rate',
    desc: 'Share of analyses requiring rerun, by test and root cause.',
    kpis: [
      k('Rerun Rate', '3.2%', '▼ 0.3pp', 'down'),
      k('Reruns (30d)', '59', '— stable', 'flat'),
      k('Top Cause', 'Calibration drift', '38% of reruns', 'flat'),
      k('Added Analyst Hours', '22.5 hrs', '▲ cost impact', 'down'),
    ],
    blocks: [
      { kind: 'panelRow', cols: '2', panels: [
        { title: 'Rerun Rate by Test', tall: true,
          chart: { type: 'bar', labels: [...TESTS], data: TESTS.map((_, i) => +(one(3, 2.5, 190 + i)).toFixed(1)), color: C.orange, horizontal: true },
          note: "Bar height is the percentage of that test's analyses that required a rerun. Higher bars mean more rework and lost analyst time." },
        { title: 'Rerun Cause Breakdown', tall: true,
          chart: { type: 'pie', donut: true, legendPos: 'right',
            labels: ['Calibration drift', 'Sample matrix interference', 'Instrument error', 'QC failure', 'Other'],
            data: [38, 24, 18, 12, 8], colors: [C.orange, C.blue, C.amber, C.red, C.grey] },
          note: 'Each slice is the share of reruns attributable to that cause. The largest slice is the best place to focus a corrective action.' },
      ] },
    ],
  };

  P['lab-oos'] = {
    title: 'OOC / OOS Trend',
    desc: 'Out-of-control and out-of-spec results over time.',
    kpis: [
      k('OOC Events (30d)', '17', '▲ 2 vs prior', 'up'),
      k('OOS Events (30d)', '6', '— stable', 'flat'),
      k('CAPA Triggered', '4', '— stable', 'flat'),
      k('Repeat Offender Method', 'EPA 300.0', '6 events', 'down'),
    ],
    blocks: [
      { kind: 'panel', panel: {
        title: 'OOC / OOS Events — 12 week trend', tall: true,
        chart: { type: 'groupBar', labels: WEEKS12, series: [
          { name: 'OOC', color: C.red, data: series(12, 1.4, 2, 200) },
          { name: 'OOS', color: C.amber, data: series(12, 0.5, 1.5, 210) },
        ] },
        legend: [{ label: 'OOC', color: C.red }, { label: 'OOS', color: C.amber }],
        note: 'Red bars are out-of-control (OOC) events and amber bars are out-of-spec (OOS) results, both by week. Persistent or growing bars suggest a method or instrument problem rather than a one-off.',
      } },
    ],
  };

  P['lab-recovery'] = {
    title: 'Surrogate/ISTD Recovery',
    desc: 'Recovery percentage trends against acceptance windows.',
    kpis: [
      k('Avg Recovery', '98.4%', '— in window', 'flat'),
      k('Out-of-Window', '2.1%', '▼ 0.4pp', 'down'),
      k('Acceptance Window', '70–130%', 'method default', 'flat'),
      k('Most Variable Surrogate', 'FBZP-d10', '±18%', 'down'),
    ],
    blocks: [
      { kind: 'panel', panel: {
        title: 'Recovery % — 28 day trend with acceptance band', tall: true,
        chart: { type: 'line', labels: DAYS28, yMin: 50, yMax: 150, legend: true, series: [
          { name: 'Recovery %', color: C.blue, data: series(28, 98, 8, 220) },
          { name: 'Upper limit', color: C.red, dashed: true, data: Array(28).fill(130) },
          { name: 'Lower limit', color: C.red, dashed: true, data: Array(28).fill(70) },
        ] },
        note: 'The blue line is daily surrogate/ISTD recovery percentage; the dashed red lines are the upper and lower acceptance limits. Points that cross outside the dashed lines are out-of-window and need review.',
      } },
    ],
  };

  P['lab-part11'] = {
    title: 'Part 11 Compliance Status',
    desc: 'Electronic signature and audit trail compliance across reporting workflow.',
    kpis: [
      k('Reports e-Signed', '99.1%', '▲ 0.3pp', 'up'),
      k('Audit Trail Gaps', '0', '— clean', 'up'),
      k('Pending Signatures', '4', '— normal queue', 'flat'),
      k('Signature SLA', '97.6%', 'within 24h', 'flat'),
    ],
    blocks: [
      { kind: 'table', table: {
        cols: ['Report ID', 'Client', 'Stage', 'Signed By', 'Timestamp', 'Status'],
        rows: Array.from({ length: 10 }, (_, i): Cell[] => {
          const r = seedRand(300 + i)();
          const stages = ['Draft', 'Submitted', 'Reviewed', 'Approved', 'Released'];
          const status = r > 0.92 ? 'Pending' : 'Signed';
          return [`RPT-${2000 + i * 7}`, pick(CLIENTS, 301 + i), pick(stages, 302 + i), pick(ANALYSTS, 303 + i),
            `2026-06-${10 + i} 14:${10 + i * 3}`, { text: status, pill: status === 'Signed' ? 'green' : 'amber' }];
        }),
      } },
    ],
  };

  /* ═══════════════ LAB · Productivity & Resources ═══════════════ */
  P['lab-analyst'] = {
    title: 'Samples per Analyst/Instrument',
    desc: 'Throughput normalized by analyst and instrument to spot load imbalance.',
    kpis: [
      k('Avg Samples/Analyst (30d)', '307', '▲ 4.8%', 'up'),
      k('Top Performer', 'S. Patel', '368 samples', 'flat'),
      k('Lowest Load', 'A. Brooks', '241 samples', 'flat'),
      k('Load Variance', '±21%', 'imbalance flag', 'down'),
    ],
    blocks: [
      { kind: 'panelRow', cols: '2', panels: [
        { title: 'Samples Processed by Analyst', tall: true,
          chart: { type: 'bar', labels: [...ANALYSTS], data: ANALYSTS.map((_, i) => one(300, 90, 400 + i)), color: C.orange, horizontal: true },
          note: 'Bar length is total samples processed by each analyst. Large gaps between bars can indicate uneven workload distribution.' },
        { title: 'Samples Processed by Instrument', tall: true,
          chart: { type: 'bar', labels: [...INSTRUMENTS], data: INSTRUMENTS.map((_, i) => one(310, 100, 410 + i)), color: C.blue, horizontal: true },
          note: 'Bar length is total samples processed on each instrument. Compare against the analyst chart to see if load is balanced across both people and equipment.' },
      ] },
    ],
  };

  P['lab-util'] = {
    title: 'Instrument Utilization & Downtime',
    desc: 'Active run time vs. idle and downtime, by instrument.',
    kpis: [
      k('Avg Utilization', '71.4%', '▲ 2.1pp', 'up'),
      k('Total Downtime (30d)', '38 hrs', '▲ 6 hrs', 'down'),
      k('Most Downtime', 'GC-MS 2', '14 hrs', 'down'),
      k('Maintenance Events', '5', '— scheduled', 'flat'),
    ],
    blocks: [
      { kind: 'panel', panel: {
        title: 'Utilization % by Instrument', tall: true,
        chart: { type: 'bar', labels: [...INSTRUMENTS], data: INSTRUMENTS.map((_, i) => Math.round(55 + one(20, 15, 420 + i))), color: C.green, yMin: 0, yMax: 100 },
        note: 'Bar height is the percentage of available time each instrument was actively running samples. Low bars suggest idle capacity; very high bars may signal a bottleneck risk.',
      } },
    ],
  };

  P['lab-mix'] = {
    title: 'Method/Test Mix Distribution',
    desc: 'Share of total lab volume by test method.',
    kpis: [
      k('Most Run Test', 'Total Coliform', '22% of volume', 'flat'),
      k('Distinct Methods Run', '10', '— stable', 'flat'),
      k('Fastest Growing', 'DBPs', '▲ 18% MoM', 'up'),
      k('Declining', 'Alkalinity', '▼ 6% MoM', 'down'),
    ],
    blocks: [
      { kind: 'panel', panel: {
        title: 'Test Mix Share', tall: true,
        chart: { type: 'pie', legendPos: 'right', labels: [...TESTS], data: [22, 16, 14, 12, 10, 8, 7, 5, 4, 2],
          colors: [C.orange, C.blue, C.green, C.amber, C.red, C.navy, C.orangeLight, C.blueLight, C.greenLight, C.grey] },
        note: "Each slice is that test's share of total lab volume. Useful for spotting which tests dominate capacity planning and where new demand is concentrated.",
      } },
    ],
  };

  P['lab-shift'] = {
    title: 'Shift / Overtime Load',
    desc: 'Regular vs. overtime hours by analyst, last pay period.',
    kpis: [
      k('Total OT Hours', '64', '▲ 12 vs prior', 'down'),
      k('% of Staff with OT', '50%', '▲ 1 analyst', 'down'),
      k('Highest OT', 'M. Reyes', '18 hrs', 'down'),
      k('OT Cost Impact', '$2,880', 'est. this period', 'down'),
    ],
    blocks: [
      { kind: 'panel', panel: {
        title: 'Regular vs. Overtime Hours by Analyst', tall: true,
        chart: { type: 'groupBar', labels: [...ANALYSTS], stacked: true, series: [
          { name: 'Regular', color: C.blue, data: ANALYSTS.map((_, i) => Math.round(70 + one(8, 6, 430 + i))) },
          { name: 'Overtime', color: C.red, data: ANALYSTS.map((_, i) => Math.round(one(8, 8, 440 + i))) },
        ] },
        legend: [{ label: 'Regular', color: C.blue }, { label: 'Overtime', color: C.red }],
        note: 'Blue is regular hours and red is overtime hours, stacked per analyst. Tall red segments flag analysts who may be overloaded.',
      } },
    ],
  };

  /* ═══════════════ LAB · Financial ═══════════════ */
  P['lab-revclient'] = {
    title: 'Revenue by Client/Test',
    desc: 'Revenue contribution by client and by test category.',
    kpis: [
      k('Total Revenue (MTD)', '$184,200', '▲ 7.4%', 'up'),
      k('Top Client', 'DWO', '$41,800', 'flat'),
      k('Top Test', 'Metals Panel', '$28,400', 'flat'),
      k('Avg Revenue/Sample', '$100', '▲ $3', 'up'),
    ],
    blocks: [
      { kind: 'panelRow', cols: '2', panels: [
        { title: 'Revenue by Client', tall: true,
          chart: { type: 'bar', labels: [...CLIENTS], data: CLIENTS.map((_, i) => one(20000, 9000, 450 + i)), color: C.orange, horizontal: true },
          note: 'Bar length is revenue generated by that client this period. Longer bars are the most valuable accounts by billing.' },
        { title: 'Revenue by Test', tall: true,
          chart: { type: 'bar', labels: [...TESTS], data: TESTS.map((_, i) => one(16000, 8000, 460 + i)), color: C.blue, horizontal: true },
          note: 'Bar length is revenue generated by that test category. Useful for seeing which services contribute most to lab income.' },
      ] },
    ],
  };

  const costVals = TESTS.map((_, i) => Math.round(25 + one(20, 15, 470 + i)));
  P['lab-costsample'] = {
    title: 'Cost per Sample/Test',
    desc: 'Fully-loaded cost (labor, reagents, instrument time) per test.',
    kpis: [
      k('Avg Cost/Sample', '$42.30', '▲ $1.10', 'down'),
      k('Highest Cost Test', 'Metals Panel', '$68/sample', 'flat'),
      k('Lowest Cost Test', 'Alkalinity', '$11/sample', 'flat'),
      k('Avg Margin', '58%', '▲ 1.2pp', 'up'),
    ],
    blocks: [
      { kind: 'panel', panel: {
        title: 'Cost vs. Price by Test', tall: true,
        chart: { type: 'groupBar', labels: [...TESTS], horizontal: true, series: [
          { name: 'Cost', color: C.red, data: costVals },
          { name: 'Price', color: C.green, data: costVals.map((c, i) => Math.round(c * (1.5 + det(470 + i) * 0.4))) },
        ] },
        legend: [{ label: 'Cost', color: C.red }, { label: 'Price', color: C.green }],
        note: 'Red bars are the fully-loaded cost to run that test; green bars are the price charged. A small gap between the bars means thin margin on that test.',
      } },
    ],
  };

  P['lab-billing'] = {
    title: 'Billing Reconciliation',
    desc: 'Completed work vs. billed work — identifies unbilled or disputed items.',
    kpis: [
      k('Completed Work Value', '$184,200', '— MTD', 'flat'),
      k('Billed', '$176,900', '95.2%', 'down'),
      k('Unbilled', '$7,300', '▲ vs prior', 'down'),
      k('Disputed Items', '3', '— open', 'flat'),
    ],
    blocks: [
      { kind: 'table', table: {
        cols: ['Client', 'Completed', 'Billed', 'Variance', 'Status'],
        rows: CLIENTS.map((c, i): Cell[] => {
          const completed = Math.round(15000 + one(8000, 6000, 480 + i));
          const billed = Math.round(completed * (0.92 + det(480 + i) * 0.08));
          const variance = completed - billed;
          const unbilled = variance > 1500;
          return [`${CLIENT_NAMES[c]} (${c})`, money(completed), money(billed), money(variance),
            { text: unbilled ? 'Unbilled gap' : 'Reconciled', pill: unbilled ? 'amber' : 'green' }];
        }),
      } },
    ],
  };

  P['lab-profit'] = {
    title: 'Client Profitability',
    desc: 'Revenue minus cost-to-serve, by client.',
    kpis: [
      k('Avg Margin', '58%', '▲ 1.2pp', 'up'),
      k('Most Profitable', 'DWO', '64% margin', 'flat'),
      k('Least Profitable', 'HHD', '41% margin', 'down'),
      k('Clients Below Target', '2', '— below 50%', 'down'),
    ],
    blocks: [
      { kind: 'panel', panel: {
        title: 'Margin % by Client', tall: true,
        chart: { type: 'bar', labels: [...CLIENTS], data: CLIENTS.map((_, i) => Math.round(40 + one(20, 15, 490 + i))), color: C.green, yMin: 0, yMax: 80 },
        note: 'Bar height is profit margin percentage for that client. Lower bars are less profitable accounts — worth a pricing or efficiency review.',
      } },
    ],
  };

  /* ═══════════════ LAB · Report & Client Service ═══════════════ */
  P['lab-reportcycle'] = {
    title: 'Report Cycle Time by Stage',
    desc: 'Time spent in each workflow stage: Draft → Submitted → Reviewed → Approved → Released.',
    kpis: [
      k('Avg Total Cycle', '3.8 days', '▲ 0.2d', 'down'),
      k('Longest Stage', 'Reviewed', '1.4 days avg', 'down'),
      k('Reports Released (30d)', '428', '▲ 5.1%', 'up'),
      k('Stuck in Review >3d', '6', '— flag', 'down'),
    ],
    blocks: [
      { kind: 'panel', panel: {
        title: 'Avg Days per Stage', tall: true,
        chart: { type: 'bar', labels: ['Draft', 'Submitted', 'Reviewed', 'Approved', 'Released'], data: [0.6, 0.8, 1.4, 0.7, 0.3], color: [C.grey, C.blue, C.orange, C.amber, C.green] },
        note: 'Bar height is the average number of days a report spends in that workflow stage. The tallest bar is where reports are most likely to stall.',
      } },
    ],
  };

  P['lab-rollback'] = {
    title: 'Report Rejection/Rollback Rate',
    desc: 'Reports sent back from Reviewed/Approved to earlier stages.',
    kpis: [
      k('Rollback Rate', '6.1%', '▲ 0.8pp', 'down'),
      k('Rollbacks (30d)', '26', '▲ 4 vs prior', 'down'),
      k('Top Reason', 'Data entry error', '42% of rollbacks', 'flat'),
      k('Added Cycle Time', '+1.2 days', 'avg per rollback', 'down'),
    ],
    blocks: [
      { kind: 'panelRow', cols: '2', panels: [
        { title: 'Rollback Rate — 12 week trend', tall: true,
          chart: { type: 'line', labels: WEEKS12, yMin: 0, yMax: 14, series: [{ color: C.red, data: series(12, 6, 2.5, 500), fill: true }] },
          note: 'This line tracks how many reports were rolled back to an earlier stage each week. A rising trend means more rework in the reporting process.' },
        { title: 'Rollback Reasons', tall: true,
          chart: { type: 'pie', donut: true, legendPos: 'right',
            labels: ['Data entry error', 'Missing QC review', 'Client request', 'Calculation error', 'Other'],
            data: [42, 22, 16, 12, 8], colors: [C.red, C.amber, C.blue, C.orange, C.grey] },
          note: 'Each slice shows the share of rollbacks caused by that reason. The largest slice is the best target for a process fix.' },
      ] },
    ],
  };

  P['lab-capa'] = {
    title: 'CAPA Tracking',
    desc: 'Corrective and preventive actions — open vs. closed status.',
    kpis: [
      k('Open CAPAs', '7', '— stable', 'flat'),
      k('Closed (30d)', '4', '▲ 1', 'up'),
      k('Avg Time to Close', '18 days', '▼ 2 days', 'up'),
      k('Overdue', '2', '— past due date', 'down'),
    ],
    blocks: [
      { kind: 'table', table: {
        cols: ['CAPA ID', 'Source', 'Opened', 'Owner', 'Due', 'Status'],
        rows: Array.from({ length: 7 }, (_, i): Cell[] => {
          const sources = ['QC Failure', 'Client Complaint', 'OOS Result', 'Audit Finding', 'Internal Review'];
          const owners = ['Selvi', 'John', 'Wilson', 'Chris'];
          const r = seedRand(510 + i)();
          const status = r > 0.75 ? (r > 0.92 ? 'Overdue' : 'Open') : 'Closed';
          const pill = status === 'Closed' ? 'green' : status === 'Overdue' ? 'red' : 'amber';
          return [`CAPA-${100 + i}`, pick(sources, 511 + i), `2026-0${5 + (i % 2)}-${10 + i}`, pick(owners, 512 + i),
            `2026-0${6 + (i % 2)}-${15 + i}`, { text: status, pill }];
        }),
      } },
    ],
  };

  /* ═══════════════ LAB · Operational Health ═══════════════ */
  P['lab-rejection'] = {
    title: 'Sample Rejection Rate',
    desc: 'Samples rejected at receipt — improper containers, missed holding time, insufficient volume.',
    kpis: [
      k('Rejection Rate', '2.4%', '▲ 0.3pp', 'down'),
      k('Rejected (30d)', '44', '▲ 6 vs prior', 'down'),
      k('Top Reason', 'Improper container', '36% of rejections', 'flat'),
      k('Worst Client', 'JEL', '9 rejections', 'down'),
    ],
    blocks: [
      { kind: 'panelRow', cols: '2', panels: [
        { title: 'Rejection Reasons', tall: true,
          chart: { type: 'pie', donut: true, legendPos: 'right',
            labels: ['Improper container', 'Missed holding time', 'Insufficient volume', 'Missing label/COC', 'Temperature excursion'],
            data: [36, 26, 18, 12, 8], colors: [C.red, C.amber, C.orange, C.blue, C.grey] },
          note: 'Each slice is the share of sample rejections caused by that issue. Use this to guide client-facing sampling training.' },
        { title: 'Rejections by Client', tall: true,
          chart: { type: 'bar', labels: [...CLIENTS], data: CLIENTS.map((_, i) => one(5, 5, 520 + i)), color: C.red },
          note: 'Bar height is the number of samples rejected from that client. Clients with consistently tall bars may need sampling guidance or kit changes.' },
      ] },
    ],
  };

  P['lab-coc'] = {
    title: 'Chain of Custody Exceptions',
    desc: 'COC documentation gaps or breaks identified at intake or review.',
    kpis: [
      k('Exceptions (30d)', '11', '▲ 2 vs prior', 'down'),
      k('Exception Rate', '0.6%', '— stable', 'flat'),
      k('Most Common', 'Missing signature', '5 of 11', 'flat'),
      k('Resolved Same-Day', '82%', '▲ 4pp', 'up'),
    ],
    blocks: [
      { kind: 'panel', panel: {
        title: 'COC Exceptions — 12 week trend', tall: true,
        chart: { type: 'bar', labels: WEEKS12, data: series(12, 1, 1.5, 530), color: C.amber },
        note: 'Each bar is the count of chain-of-custody exceptions found that week. Occasional low bars are normal; a sustained increase points to a documentation process gap.',
      } },
    ],
  };

  /* ═══════════════ SALES · Pipeline & Growth ═══════════════ */
  P['sales-newclients'] = {
    title: 'New Client Acquisition',
    desc: 'New clients signed per month against target.',
    kpis: [
      k('New Clients (YTD)', '5', '▲ vs 3 LY', 'up'),
      k('This Month', '1', '— on pace', 'flat'),
      k('Avg Time to Close', '42 days', '▼ 6 days', 'up'),
      k('YTD Target', '8', '63% of target', 'down'),
    ],
    blocks: [
      { kind: 'panel', panel: {
        title: 'New Clients per Month — 12 months', tall: true,
        chart: { type: 'bar', labels: MONTHS, data: [0, 1, 0, 1, 1, 0, 0, 1, 0, 1, 0, 0], color: C.orange },
        note: 'Bar height is the number of new clients signed that month. Use this against the YTD target to gauge whether new-business pace is on track.',
      } },
    ],
  };

  P['sales-pipeline'] = {
    title: 'Sales Pipeline by Stage',
    desc: 'Active opportunities by stage — Lead → Qualified → Quote → Negotiation → Won/Lost.',
    kpis: [
      k('Open Opportunities', '18', '— stable', 'flat'),
      k('Pipeline Value', '$412K', '▲ 9%', 'up'),
      k('Largest Deal', '$86K', 'El Paso Water', 'flat'),
      k('Stalled >30 Days', '3', '— flag', 'down'),
    ],
    blocks: [
      { kind: 'panel', panel: {
        title: 'Opportunities by Stage', tall: true,
        chart: { type: 'bar', horizontal: true, labels: ['Lead', 'Qualified', 'Quote Sent', 'Negotiation', 'Won'], data: [18, 12, 8, 5, 3],
          color: [C.grey, C.blueLight, C.blue, C.orange, C.green] },
        note: 'Each bar is the number of open opportunities at that stage, ordered earliest to latest. The drop-off between bars shows where deals are most likely to fall out of the pipeline.',
      } },
    ],
  };

  P['sales-winrate'] = {
    title: 'Win Rate',
    desc: 'Won vs. lost opportunities, by client type and test category.',
    kpis: [
      k('Overall Win Rate', '38%', '▲ 3pp', 'up'),
      k('Won (YTD)', '5', '— stable', 'flat'),
      k('Lost (YTD)', '8', '— stable', 'flat'),
      k('Best Segment', 'Municipal Water', '52% win rate', 'up'),
    ],
    blocks: [
      { kind: 'panelRow', cols: '2', panels: [
        { title: 'Win Rate Trend — 12 months', tall: true,
          chart: { type: 'line', labels: MONTHS, yMin: 0, yMax: 60, series: [{ color: C.green, data: series(12, 36, 8, 600), fill: true }] },
          note: 'This line tracks the percentage of opportunities won each month. A rising line means sales effectiveness is improving.' },
        { title: 'Win Rate by Segment', tall: true,
          chart: { type: 'bar', horizontal: true, yMin: 0, yMax: 60, labels: ['Municipal Water', 'Industrial', 'Environmental Consulting', 'Private Utility'], data: [52, 34, 29, 41], color: C.blue },
          note: 'Bar length is the win rate for that client segment. Segments with longer bars are where the sales pitch and pricing are landing best.' },
      ] },
    ],
  };

  P['sales-quotetime'] = {
    title: 'Quote Turnaround Time',
    desc: 'Time from request to delivered quote.',
    kpis: [
      k('Avg Turnaround', '3.4 days', '▼ 0.6d', 'up'),
      k('Fastest Rep', 'Wilson T.', '2.1 days avg', 'flat'),
      k('Quotes Sent (30d)', '14', '— stable', 'flat'),
      k('Slowest Quote Type', 'Custom panel', '5.8d avg', 'down'),
    ],
    blocks: [
      { kind: 'panel', panel: {
        title: 'Quote Turnaround — 12 week trend', tall: true,
        chart: { type: 'line', labels: WEEKS12, yMin: 0, yMax: 7, series: [{ color: C.orange, data: series(12, 3.5, 1.2, 610) }] },
        note: 'This line tracks average days to deliver a quote, by week. A downward trend means quotes are going out faster, which generally improves win rate.',
      } },
    ],
  };

  P['sales-lostreasons'] = {
    title: 'Lost Business Reasons',
    desc: 'Why opportunities were lost — pricing, capacity, competitor, timing.',
    kpis: [
      k('Lost Deals (YTD)', '8', '— stable', 'flat'),
      k('Lost Value', '$196K', '▲ 14%', 'down'),
      k('Top Reason', 'Price', '37% of losses', 'flat'),
      k('Top Competitor', 'Eurofins', '3 losses', 'flat'),
    ],
    blocks: [
      { kind: 'panel', panel: {
        title: 'Lost Reasons Breakdown', tall: true,
        chart: { type: 'pie', donut: true, legendPos: 'right',
          labels: ['Price', 'Capacity/timeline', 'Lost to competitor', 'Scope mismatch', 'Budget cut'],
          data: [37, 24, 19, 12, 8], colors: [C.red, C.amber, C.blue, C.orange, C.grey] },
        note: 'Each slice is the share of lost deals attributed to that reason. The largest slice is the most common objection sales is currently losing to.',
      } },
    ],
  };

  /* ═══════════════ SALES · Revenue Analytics ═══════════════ */
  P['sales-revclient'] = {
    title: 'Revenue by Client',
    desc: 'Top clients by trailing-12-month revenue.',
    kpis: [
      k('Total Revenue (TTM)', '$2.1M', '▲ 8.4%', 'up'),
      k('Top Client', 'DWO', '$498K', 'flat'),
      k('Top 3 Concentration', '52%', 'concentration risk', 'down'),
      k('Avg Revenue/Client', '$262K', '▲ 5.1%', 'up'),
    ],
    blocks: [
      { kind: 'panel', panel: {
        title: 'TTM Revenue by Client', tall: true,
        chart: { type: 'bar', horizontal: true, labels: [...CLIENTS], data: CLIENTS.map((_, i) => one(250000, 100000, 620 + i)), color: C.orange },
        note: 'Bar length is trailing-12-month revenue from that client. Longer bars are the largest accounts by revenue.',
      } },
    ],
  };

  P['sales-revtest'] = {
    title: 'Revenue by Test/Department',
    desc: 'Revenue contribution by test category.',
    kpis: [
      k('Top Category', 'Metals Panel', '$312K TTM', 'flat'),
      k('Fastest Growing', 'DBPs', '▲ 22% YoY', 'up'),
      k('Declining', 'Alkalinity', '▼ 4% YoY', 'down'),
      k('Departments', '4', '— stable', 'flat'),
    ],
    blocks: [
      { kind: 'panel', panel: {
        title: 'Revenue by Test Category', tall: true,
        chart: { type: 'bar', horizontal: true, labels: [...TESTS], data: TESTS.map((_, i) => one(180000, 90000, 630 + i)), color: C.blue },
        note: 'Bar length is trailing-12-month revenue from that test category. Helps identify which services drive the most top-line revenue.',
      } },
    ],
  };

  P['sales-revrep'] = {
    title: 'Revenue by Sales Rep',
    desc: 'Closed revenue attributed by sales rep.',
    kpis: [
      k('Top Rep', 'Chris D.', '$1.1M TTM', 'flat'),
      k('Team Total (TTM)', '$2.1M', '▲ 8.4%', 'up'),
      k('Avg Deal Size', '$41K', '▲ 6%', 'up'),
      k('Reps Above Quota', '2 of 3', '— stable', 'flat'),
    ],
    blocks: [
      { kind: 'panel', panel: {
        title: 'TTM Revenue by Rep', tall: true,
        chart: { type: 'bar', labels: REPS, data: REPS.map((_, i) => one(700000, 300000, 640 + i)), color: C.green },
        note: 'Bar height is trailing-12-month revenue closed by that rep. Compare against the quota chart to see attainment, not just raw totals.',
      } },
    ],
  };

  P['sales-recurring'] = {
    title: 'Recurring vs. One-Time Revenue',
    desc: 'Contract/recurring testing programs vs. one-off project work.',
    kpis: [
      k('Recurring Share', '74%', '▲ 2pp', 'up'),
      k('Recurring Revenue (TTM)', '$1.55M', '▲ 7%', 'up'),
      k('One-Time Revenue (TTM)', '$550K', '— stable', 'flat'),
      k('Contracts Up for Renewal', '3', 'next 90 days', 'flat'),
    ],
    blocks: [
      { kind: 'panel', panel: {
        title: 'Recurring vs One-Time — 12 month trend', tall: true,
        chart: { type: 'groupBar', labels: MONTHS, stacked: true, series: [
          { name: 'Recurring', color: C.blue, data: series(12, 128000, 15000, 650) },
          { name: 'One-Time', color: C.orange, data: series(12, 46000, 12000, 660) },
        ] },
        legend: [{ label: 'Recurring', color: C.blue }, { label: 'One-Time', color: C.orange }],
        note: 'Blue is recurring/contract revenue and orange is one-time project revenue, stacked by month. A growing blue band means the revenue base is becoming more predictable.',
      } },
    ],
  };

  P['sales-yoy'] = {
    title: 'YoY / MoM Growth',
    desc: 'Revenue growth rates, year-over-year and month-over-month.',
    kpis: [
      k('YoY Growth', '8.4%', '▲ vs 5.1% LY', 'up'),
      k('MoM Growth', '1.2%', '— steady', 'flat'),
      k('Best Month', 'March', '+14% MoM', 'up'),
      k('12mo Trailing Revenue', '$2.1M', '▲ 8.4%', 'up'),
    ],
    blocks: [
      { kind: 'panel', panel: {
        title: 'Revenue — This Year vs Last Year', tall: true,
        chart: { type: 'line', labels: MONTHS, legend: true, series: [
          { name: 'This Year', color: C.orange, data: series(12, 178000, 20000, 670) },
          { name: 'Last Year', color: C.grey, dashed: true, data: series(12, 164000, 18000, 680) },
        ] },
        legend: [{ label: 'This Year', color: C.orange }, { label: 'Last Year', color: C.grey }],
        note: "The orange line is this year's monthly revenue; the dashed grey line is last year's. Orange consistently above grey means the business is growing year-over-year.",
      } },
    ],
  };

  /* ═══════════════ SALES · Client Behavior ═══════════════ */
  P['sales-retention'] = {
    title: 'Client Retention / Churn',
    desc: 'Annual retention rate and churned clients.',
    kpis: [
      k('Retention Rate', '92%', '▲ 2pp', 'up'),
      k('Churned (TTM)', '1', '— stable', 'flat'),
      k('Revenue Churned', '$38K', '— stable', 'flat'),
      k('At-Risk Clients', '1', '— monitor', 'down'),
    ],
    blocks: [
      { kind: 'panel', panel: {
        title: 'Retention Rate — 12 month trend', tall: true,
        chart: { type: 'line', labels: MONTHS, yMin: 80, yMax: 100, series: [{ color: C.green, data: series(12, 91, 2, 700), fill: true }] },
        note: 'This line tracks client retention rate by month. A flat line near 100% is ideal; dips indicate clients leaving or going inactive.',
      } },
    ],
  };

  P['sales-voltrend'] = {
    title: 'Sample Volume Trend per Client',
    desc: 'Per-client monthly volume — early warning for drop-off.',
    kpis: [
      k('Clients Trending Up', '5 of 8', '— healthy', 'up'),
      k('Clients Trending Down', '2 of 8', '— monitor', 'down'),
      k('Biggest Decline', 'HHD', '▼ 18% 3mo', 'down'),
      k('Biggest Gain', 'DWO', '▲ 12% 3mo', 'up'),
    ],
    blocks: [
      { kind: 'panel', panel: {
        title: 'Monthly Volume per Client — 12 months', tall: true,
        chart: { type: 'line', labels: MONTHS, legend: true,
          series: CLIENTS.map((c, i) => ({ name: c, color: CLIENT_COLORS[i] ?? C.grey, data: series(12, 35, 10, 710 + i * 3) })) },
        note: "Each colored line is one client's monthly sample volume. A line trending downward over several months is an early warning sign worth a check-in call.",
      } },
    ],
  };

  P['sales-aov'] = {
    title: 'Average Order Value',
    desc: 'Average value per work order, by client.',
    kpis: [
      k('Avg Order Value', '$1,840', '▲ 4.2%', 'up'),
      k('Highest AOV', 'DWO', '$2,640', 'flat'),
      k('Lowest AOV', 'JEL', '$980', 'flat'),
      k('Orders (30d)', '112', '— stable', 'flat'),
    ],
    blocks: [
      { kind: 'panel', panel: {
        title: 'AOV by Client', tall: true,
        chart: { type: 'bar', labels: [...CLIENTS], data: CLIENTS.map((_, i) => Math.round(1200 + one(800, 500, 720 + i))), color: C.orange },
        note: 'Bar height is the average value per order for that client. Higher bars mean that client typically orders larger or more complex test panels.',
      } },
    ],
  };

  P['sales-tenure'] = {
    title: 'Client Tenure Distribution',
    desc: 'How long clients have been active, in years.',
    kpis: [
      k('Avg Tenure', '4.6 yrs', '▲ 0.4 yrs', 'up'),
      k('Longest Tenured', 'DWO', '9 years', 'flat'),
      k('Newest Client', 'JEL', '8 months', 'flat'),
      k('Clients <1 Year', '1', '— onboarding', 'flat'),
    ],
    blocks: [
      { kind: 'panel', panel: {
        title: 'Tenure by Client (years)', tall: true,
        chart: { type: 'bar', labels: [...CLIENTS], data: CLIENTS.map((_, i) => +(one(4.5, 4, 730 + i)).toFixed(1)), color: C.blue },
        note: 'Bar height is how many years that client has been active. Longer-tenured clients are typically lower-risk, more predictable revenue.',
      } },
    ],
  };

  /* ═══════════════ SALES · Forecasting & Targets ═══════════════ */
  const fcActual = series(12, 175000, 18000, 740);
  P['sales-forecast'] = {
    title: 'Revenue Forecast vs Actual', badge: 'ai',
    desc: 'Projected revenue based on pipeline and historical conversion, against actuals.',
    kpis: [
      k('Q3 Forecast', '$548K', '— projected', 'flat'),
      k('Q2 Actual', '$521K', 'vs $505K forecast', 'up'),
      k('Forecast Accuracy', '96.8%', '▲ 1.4pp', 'up'),
      k('Confidence', 'High', 'based on pipeline maturity', 'flat'),
    ],
    blocks: [
      { kind: 'panel', panel: {
        title: 'Forecast vs Actual — 12 months', tall: true,
        chart: { type: 'line', labels: MONTHS, legend: true, series: [
          { name: 'Forecast', color: C.blue, dashed: true, data: fcActual.map((v, i) => Math.round(v * (0.96 + det(740 + i) * 0.06))) },
          { name: 'Actual', color: C.orange, data: fcActual },
        ] },
        legend: [{ label: 'Forecast', color: C.blue }, { label: 'Actual', color: C.orange }],
        note: 'The dashed blue line is forecasted revenue and the solid orange line is actual revenue, by month. Lines that track closely together mean the forecast model is reliable.',
      } },
    ],
  };

  P['sales-quota'] = {
    title: 'Sales Rep Performance vs Quota',
    desc: 'Closed revenue against assigned quota, by rep.',
    kpis: [
      k('Team Attainment', '91%', '— near target', 'flat'),
      k('Top Attainment', 'Chris D.', '118%', 'up'),
      k('Below Quota', '1 of 3', '— coaching flag', 'down'),
      k('Team Quota (TTM)', '$2.3M', '— set', 'flat'),
    ],
    blocks: [
      { kind: 'panel', panel: {
        title: 'Actual vs Quota by Rep', tall: true,
        chart: { type: 'groupBar', labels: REPS, series: [
          { name: 'Actual', color: C.orange, data: REPS.map((_, i) => one(700000, 200000, 750 + i)) },
          { name: 'Quota', color: C.grey, data: [750000, 700000, 650000] },
        ] },
        legend: [{ label: 'Actual', color: C.orange }, { label: 'Quota', color: C.grey }],
        note: 'Orange bars are actual revenue closed; grey bars are assigned quota, by rep. An orange bar taller than its grey pair means that rep is over quota.',
      } },
    ],
  };

  const seasonalBase = [140, 150, 210, 230, 260, 220, 180, 160, 150, 145, 140, 135];
  P['sales-seasonal'] = {
    title: 'Seasonal Demand Forecasting', badge: 'ai',
    desc: 'Projected sample volume by season, reflecting compliance reporting cycles.',
    kpis: [
      k('Peak Season', 'Q2', 'compliance cycle', 'flat'),
      k('Peak vs Trough Variance', '+34%', '— seasonal', 'flat'),
      k('Capacity Risk Window', 'May–Jun', '— plan staffing', 'down'),
      k('Forecast Confidence', 'High', '3yr historical pattern', 'flat'),
    ],
    blocks: [
      { kind: 'panel', panel: {
        title: 'Historical Volume Pattern — 24 months', tall: true,
        chart: { type: 'line', labels: [...MONTHS, ...MONTHS],
          series: [{ color: C.navy, fill: true, data: Array.from({ length: 24 }, (_, i) => Math.round((seasonalBase[i % 12] ?? 0) + one(0, 15, 760 + i))) }] },
        note: 'This line shows historical sample volume across 24 months, repeating the same seasonal shape each year. Peaks indicate the busiest compliance-driven periods to plan staffing around.',
      } },
    ],
  };

  /* ═══════════════ SALES · Cross-Sell / Upsell ═══════════════ */
  P['sales-whitespace'] = {
    title: 'Test Mix Whitespace Analysis', badge: 'ai',
    desc: "Tests in the catalog each client isn't currently ordering — upsell opportunity.",
    kpis: [
      k('Avg Catalog Coverage', '61%', 'of available tests', 'flat'),
      k('Highest Whitespace', 'JEL', '6 of 10 tests untapped', 'flat'),
      k('Est. Upsell Potential', '$184K', 'annualized', 'up'),
      k('Best Opportunity', 'DBPs @ HHD', '— suggested', 'flat'),
    ],
    blocks: [
      { kind: 'panel', panel: {
        title: 'Catalog Coverage % by Client', tall: true,
        chart: { type: 'bar', labels: [...CLIENTS], data: CLIENTS.map((_, i) => Math.round(50 + one(25, 20, 770 + i))), color: C.amber, yMin: 0, yMax: 100 },
        note: 'Bar height is the percentage of the test catalog that client already orders. Shorter bars mean more untapped tests — bigger upsell opportunity.',
      } },
    ],
  };

  P['sales-renewals'] = {
    title: 'Contract Renewal Tracking',
    desc: 'Upcoming contract expirations and renewal status.',
    kpis: [
      k('Renewing Next 90 Days', '3', '— on schedule', 'flat'),
      k('Contract Value at Stake', '$612K', 'annualized', 'flat'),
      k('Renewal Confirmed', '1 of 3', '— in progress', 'flat'),
      k('At Risk of Non-Renewal', '1', '— flag', 'down'),
    ],
    blocks: [
      { kind: 'table', table: {
        cols: ['Client', 'Contract Value', 'Expires', 'Renewal Status'],
        rows: ([
          { c: 'DWO', val: '$298K', exp: '2026-08-15', status: 'Confirmed' },
          { c: 'COA', val: '$184K', exp: '2026-09-01', status: 'In negotiation' },
          { c: 'RRA', val: '$130K', exp: '2026-09-20', status: 'At risk' },
        ] as const).map((r): Cell[] => [
          `${CLIENT_NAMES[r.c]} (${r.c})`, r.val, r.exp,
          { text: r.status, pill: r.status === 'Confirmed' ? 'green' : r.status === 'At risk' ? 'red' : 'amber' },
        ]),
      } },
    ],
  };

  const prQuoted = CLIENTS.map((_, i) => Math.round(20000 + one(8000, 6000, 780 + i)));
  P['sales-pricerealization'] = {
    title: 'Price Realization',
    desc: 'Quoted price vs. actually billed price — discounting leakage.',
    kpis: [
      k('Price Realization', '94.2%', '▼ 1.1pp', 'down'),
      k('Avg Discount', '5.8%', '▲ 1.1pp', 'down'),
      k('Largest Discount Client', 'DWO', '9.4%', 'down'),
      k('Revenue Leakage (TTM)', '$122K', '— review', 'down'),
    ],
    blocks: [
      { kind: 'panel', panel: {
        title: 'Quoted vs Billed Price by Client', tall: true,
        chart: { type: 'groupBar', labels: [...CLIENTS], series: [
          { name: 'Quoted', color: C.blue, data: prQuoted },
          { name: 'Billed', color: C.orange, data: prQuoted.map((v, i) => Math.round(v * (0.88 + det(780 + i) * 0.08))) },
        ] },
        legend: [{ label: 'Quoted', color: C.blue }, { label: 'Billed', color: C.orange }],
        note: 'Blue bars are quoted price and orange bars are what was actually billed, by client. A large gap between the bars means heavier discounting for that account.',
      } },
    ],
  };

  return P;
}
